<template>
  <div class="min-h-screen bg-gradient-to-br from-accent-50 via-white to-primary-50">
    <!-- Hero section -->
    <div class="relative overflow-hidden">
      <!-- Background pattern -->
      <div class="absolute inset-0 bg-gradient-to-r from-primary-600 via-primary-700 to-navy-800"></div>
      <div class="absolute inset-0 bg-black/10"></div>
      
      <!-- Decorative elements -->
      <div class="absolute top-0 left-0 w-full h-full">
        <div class="absolute top-20 left-10 w-32 h-32 bg-gold-400/20 rounded-full blur-3xl"></div>
        <div class="absolute bottom-20 right-10 w-40 h-40 bg-accent-400/20 rounded-full blur-3xl"></div>
        <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-white/10 rounded-full blur-3xl"></div>
      </div>
      
      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div class="text-center animate-fade-in">
          <h1 class="hero-title text-white mb-6">
            Notre <span class="bg-gradient-to-r from-gold-400 to-gold-300 bg-clip-text text-transparent">Agenda</span>
          </h1>
        </div>
      </div>
    </div>
    
    <!-- Performances content -->
    <div class="py-16">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Filter tabs -->
        <div class="flex justify-center mb-16">
          <div class="card-glass p-2 shadow-xl">
            <button 
              @click="activeTab = 'upcoming'"
              class="px-8 py-3 rounded-xl text-base font-semibold transition-all duration-300 hover:scale-105"
              :class="activeTab === 'upcoming' 
                ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg' 
                : 'text-navy-600 hover:text-primary-600 hover:bg-white/50'"
            >
              À venir
            </button>
            <button 
              @click="activeTab = 'all'"
              class="px-8 py-3 rounded-xl text-base font-semibold transition-all duration-300 hover:scale-105"
              :class="activeTab === 'all' 
                ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg' 
                : 'text-navy-600 hover:text-primary-600 hover:bg-white/50'"
            >
              Toutes
            </button>
          </div>
        </div>
        
        <!-- Loading state -->
        <div v-if="loading" class="flex flex-col items-center justify-center py-20">
          <div class="relative">
            <div class="animate-spin rounded-full h-16 w-16 border-4 border-accent-200"></div>
            <div class="animate-spin rounded-full h-16 w-16 border-4 border-accent-600 border-t-transparent absolute top-0 left-0"></div>
          </div>
          <p class="mt-6 text-navy-600 text-lg font-medium">Chargement de l'agenda...</p>
        </div>
        
        <!-- Error state -->
        <div v-else-if="error" class="text-center py-20">
          <div class="card-gradient max-w-md mx-auto">
            <div class="p-8">
              <div class="w-16 h-16 bg-accent-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg class="w-8 h-8 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                </svg>
              </div>
              <h3 class="text-xl font-bold text-navy-800 mb-4">Oups !</h3>
              <p class="text-navy-600 mb-6">Impossible de charger l'agenda. Veuillez réessayer plus tard.</p>
              <button @click="loadPerformances" class="btn-primary">Réessayer</button>
            </div>
          </div>
        </div>
        
        <!-- Performances grid -->
        <div v-else-if="filteredPerformances.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div 
            v-for="performance in filteredPerformances" 
            :key="performance.id"
            class="group hover-lift"
          >
            <div class="card-gradient group-hover:shadow-2xl transition-all duration-500 group-hover:scale-105">
              <div class="p-8">
                <!-- Date badge -->
                <div class="flex items-center mb-6">
                  <div 
                    class="px-4 py-2 rounded-2xl text-sm font-bold shadow-lg"
                    :class="isUpcoming(performance.date) 
                      ? 'bg-gradient-to-r from-green-500 to-green-600 text-white' 
                      : 'bg-gradient-to-r from-navy-200 to-navy-300 text-navy-800'"
                  >
                    {{ formatDate(performance.date) }}
                  </div>
                </div>
                
                <!-- Performance info -->
                <h3 class="text-2xl font-bold text-navy-800 mb-4 group-hover:text-primary-600 transition-colors duration-300">
                  {{ performance.title }}
                </h3>
                
                <p v-if="performance.description" class="text-navy-600 mb-6 line-clamp-3 leading-relaxed">
                  {{ performance.description }}
                </p>
                
                <!-- Details -->
                <div class="space-y-3 text-sm">
                  <div v-if="performance.location" class="flex items-center text-navy-600">
                    <div class="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <svg class="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                      </svg>
                    </div>
                    <span class="font-medium">{{ performance.location }}</span>
                  </div>
                  <div v-if="performance.price" class="flex items-center text-navy-600">
                    <div class="w-8 h-8 bg-gold-100 rounded-lg flex items-center justify-center mr-3">
                      <span class="text-gold-600 font-bold text-lg">€</span>
                    </div>
                    <span class="font-medium">{{ performance.price }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Empty state -->
        <div v-else class="text-center text-gray-500">
          <p v-if="activeTab === 'upcoming'">
            Aucune prestation prévue pour le moment.
          </p>
          <p v-else>
            Aucune prestation disponible pour le moment.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { performanceService } from '../services/performanceService'

export default {
  name: 'Performances',
  data() {
    return {
      performances: [],
      upcomingPerformances: [],
      loading: true,
      error: false,
      activeTab: 'upcoming'
    }
  },
  computed: {
    filteredPerformances() {
      return this.activeTab === 'upcoming' ? this.upcomingPerformances : this.performances
    }
  },
  async mounted() {
    await this.loadPerformances()
  },
  methods: {
    async loadPerformances() {
      try {
        this.loading = true
        this.error = false
        
        const [performances, upcoming] = await Promise.all([
          performanceService.getPerformances(),
          performanceService.getUpcomingPerformances()
        ])
        
        this.performances = performances
        this.upcomingPerformances = upcoming
      } catch (error) {
        console.error('Error loading performances:', error)
        this.error = true
      } finally {
        this.loading = false
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    isUpcoming(dateString) {
      const date = new Date(dateString)
      const now = new Date()
      return date >= now
    }
  }
}
</script>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
